import { Controller } from "@hotwired/stimulus"

/**
 * Desktop Carousel Controller
 * Optimized for desktop: Shows multiple slides with partial slide preview
 */
export default class extends Controller {
  static targets = ["slide", "container"]
  
  connect() {
    // Initialize variables
    this.currentPage = 2
    this.slideWidth = 160 // Each testimonial slide is 160px wide
    this.slideMargin = 4 // 2px margin on each side (mx-2 = 4px total)
    this.slidesPerPage = 3 // Desktop shows 3 main slides
    this.visibleSlides = 5 // Desktop shows 3 main + 2 partial
    this.slidesToMove = 1 
    this.totalPages = Math.max(0, this.slideTargets.length - this.slidesPerPage) + 1
    
    // Initial positioning with slight delay to ensure DOM is ready
    setTimeout(() => {
      this.updateView(true)
    }, 100)
    
    // Set up event listeners
    window.addEventListener('resize', this.handleResize.bind(this))
  }
  
  // Handle window resize
  handleResize() {
    // Desktop always keeps the same settings regardless of exact size
    // We just need to update the view to ensure proper positioning
    this.updateView(true)
  }
  
  // Navigation methods
  next() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++
      this.updateView()
    }
  }
  
  prev() {
    if (this.currentPage > 1) {
      this.currentPage--
      this.updateView()
    }
  }
  
  // Desktop-optimized update view method
  updateView(force = false) {
    const slideWidth = this.slideWidth
    const slideMargin = this.slideMargin
    const slideUnitWidth = slideWidth + slideMargin
    
    // Get the actual container width (parent viewport)
    const containerWidth = this.containerTarget.parentElement.offsetWidth
    
    // For desktop, we want to center the group of 3 active slides
    // and show partial views of previous and next slides
    
    // Calculate the space needed for 3 active slides (center group)
    const activeGroupWidth = (this.slidesPerPage * slideWidth) + ((this.slidesPerPage - 1) * slideMargin)
    
    // We need to account for the padding on each side
    const sidePadding = 32  // 16px on each side from the px-16 class
    
    // Center the active group in the container, accounting for padding
    // Start with a centering position for the active group
    let baseOffset = (containerWidth - activeGroupWidth) / 2
    
    // Add extra space to ensure next testimonial isn't cut off
    // This shifts the entire carousel slightly to the left
    const extraOffsetLeft = 30
    
    // Then offset by the current page (1-based)
    // For page 1, show slides 0,1,2 centered
    // For page 2, show slides 1,2,3 centered, etc.
    let translateX = baseOffset - ((this.currentPage - 1) * slideUnitWidth) - extraOffsetLeft
    
    console.log('Desktop view:', { 
      containerWidth,
      activeGroupWidth,
      baseOffset,
      currentPage: this.currentPage,
      translateX
    })
    
    // Apply transform to move slides
    this.containerTarget.style.transform = `translateX(${translateX}px)`
    
    // Set explicit width on container to fit all slides
    const totalContentWidth = this.slideTargets.length * slideUnitWidth
    this.containerTarget.style.width = `${totalContentWidth}px`
    
    // Ensure the main carousel container doesn't overflow
    this.containerTarget.style.minWidth = `${totalContentWidth}px`
    
    // Apply slide shrinking functionality - highlight current slides, shrink others
    this.slideTargets.forEach((slide, index) => {
      // Calculate which slides are active based on current page
      // We want 3 active slides centered around currentPage
      const activeSlideStart = this.currentPage - 1
      const activeSlideEnd = activeSlideStart + this.slidesPerPage - 1
      const isActive = index >= activeSlideStart && index <= activeSlideEnd
      
      // Apply scale transform to shrink non-active slides
      if (isActive) {
        slide.style.transform = 'scale(1)'
        slide.style.opacity = '1'
      } else {
        slide.style.transform = 'scale(0.85)'
        slide.style.opacity = '0.7'
      }
    })
  }
}
